import { Alert, Button, Timeline, type TimelineItem } from "@/shared/rd";
import type { AppConfigView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@admin/app/locale";
import { keys } from "@/lib/i18n/portal";
import { useAppConfigAuditQuery } from "../services/useAppConfigAuditQuery";
import { defaultFor, formatDateTime, formatValue } from "../lib/configValue";

type Props = {
  config: AppConfigView;
};

/**
 * A key's change history, newest first, as the drawer's timeline: what it
 * changed from and to, and who changed it. A registry key's timeline ends on
 * its environment default, where every key starts. The audit query runs while
 * this is mounted (the drawer is open).
 */
export function AppConfigAuditPanel({ config }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const { data, isLoading, error, refetch, isRefetching } = useAppConfigAuditQuery(config.key, true);
  const isCustom = config.origin === "custom";

  if (isLoading) {
    return <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>{t(keys.common.loading)}</span>;
  }
  // A failed load says so, with a retry: it never reads as "no changes".
  if (error || !data) {
    return (
      <Alert
        tone="error"
        action={
          <Button variant="ghost" size="sm" onClick={() => void refetch()} loading={isRefetching} loadingLabel={t(keys.shell.retrying)}>
            {t(keys.shell.retry)}
          </Button>
        }
      >
        {t(keys.shell.errorTitle, { page: t("appConfigs.historyPage") })}
      </Alert>
    );
  }
  if (data.length === 0) {
    return (
      <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
        {isCustom ? t(keys.appConfigs.historyEmpty) : t("appConfigs.historyNone")}
      </span>
    );
  }

  const items: TimelineItem[] = data.map((entry) => ({
    id: entry.id,
    time: formatDateTime(entry.changedAt, locale),
    tone: "blue",
    label:
      entry.newValue == null ? (
        t(keys.appConfigs.resetAction)
      ) : entry.oldValue == null ? (
        t("appConfigs.historySet", { value: formatValue(config, entry.newValue, t) })
      ) : (
        <span dir="ltr">{`${formatValue(config, entry.oldValue, t)} → ${formatValue(config, entry.newValue, t)}`}</span>
      ),
    note: entry.actorEmail ? (
      <span dir="ltr">{t("appConfigs.historyBy", { actor: entry.actorEmail })}</span>
    ) : undefined,
  }));
  if (!isCustom) {
    items.push({
      id: "env-default",
      time: t("appConfigs.historyStart"),
      tone: "idle",
      label: t("appConfigs.sourceEnvDefault"),
      note: formatValue(config, defaultFor(config), t),
    });
  }
  return <Timeline items={items} />;
}
